import { useState } from 'react';

import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PaymentsIcon from '@mui/icons-material/Payments';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AttachEmailIcon from '@mui/icons-material/AttachEmail';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

import Block from '@mui/icons-material/Block';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Undo from '@mui/icons-material/Undo';
import DeleteIcon from '@mui/icons-material/Delete';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { useCheckPermission } from 'hooks-ts/usePermission';
import { type Invoice, CORE_PERMISSIONS, INVOICE_PAYMENT, INVOICE_STATUS, type InvoiceWithBatch } from '@a1/shared';
import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import { useGeneralInvoicingAndPaymentsEnabled } from 'hooks/usePayments';

interface ActionsColumnProps {
  invoice: Invoice | InvoiceWithBatch;
  setSelectedInvoice: React.Dispatch<React.SetStateAction<Invoice | InvoiceWithBatch | null>>;
  setOpenInvoicePaymentsDialog?: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenInvoicePdfDialog?: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenSendInvoicePdfDialog?: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenMarkAsPaidDialog?: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenMarkAsDraftDialog?: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenVoidDialog?: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenDeleteInvoiceDialog?: React.Dispatch<React.SetStateAction<boolean>>;
}

const ActionsColumn = ({
  invoice,
  setSelectedInvoice,
  setOpenInvoicePaymentsDialog,
  setOpenInvoicePdfDialog,
  setOpenSendInvoicePdfDialog,
  setOpenMarkAsPaidDialog,
  setOpenMarkAsDraftDialog,
  setOpenVoidDialog,
  setOpenDeleteInvoiceDialog
}: ActionsColumnProps) => {
  const generalInvoicingAndPaymentsEnabled = useGeneralInvoicingAndPaymentsEnabled();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const open = Boolean(anchorEl);

  const handleOpenActionMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleCloseActionMenu = () => {
    setAnchorEl(null);
  };

  const permissionCheck = useCheckPermission();

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <IconButton
        id={`open-action-menu-button-${invoice.id}`}
        aria-controls={open ? 'action-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        aria-label="open action menu"
        onClick={handleOpenActionMenu}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id={`action-menu-${invoice.id}`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseActionMenu}
        slotProps={{
          list: {
            'aria-labelledby': `open-action-menu-button-${invoice.id}`
          }
        }}
      >
        {setOpenInvoicePaymentsDialog && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              if (!permissionCheck.canViewPaymentHistory()) {
                enqueueSnackbar({
                  variant: 'error',
                  message: 'You do not have permission to view invoice payments.',
                  anchorOrigin: { vertical: 'top', horizontal: 'right' }
                });
                return;
              }
              setSelectedInvoice(invoice);
              setOpenInvoicePaymentsDialog(true);
              handleCloseActionMenu();
            }}
          >
            <ListItemIcon>
              <PaymentsIcon fontSize="small" />
            </ListItemIcon>
            View Invoice Payments
          </MenuItem>
        )}

        {invoice.isLinkedToCreditNote && generalInvoicingAndPaymentsEnabled && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/manage-invoices/${invoice.id}/credit-notes`);
              handleCloseActionMenu();
            }}
          >
            <ListItemIcon>
              <ReceiptLongIcon fontSize="small" />
            </ListItemIcon>
            View Credit Notes
          </MenuItem>
        )}

        {setOpenInvoicePdfDialog && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              if (!permissionCheck.canExportSendInvoices()) {
                enqueueSnackbar({
                  variant: 'error',
                  message: 'You do not have permission to view invoice PDFs.',
                  anchorOrigin: { vertical: 'top', horizontal: 'right' }
                });
                return;
              }
              setSelectedInvoice(invoice);
              setOpenInvoicePdfDialog(true);
              handleCloseActionMenu();
            }}
          >
            <ListItemIcon>
              <PictureAsPdfIcon fontSize="small" />
            </ListItemIcon>
            View Invoice PDF
          </MenuItem>
        )}

        {setOpenSendInvoicePdfDialog && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              if (!permissionCheck.canExportSendInvoices()) {
                enqueueSnackbar({
                  variant: 'error',
                  message: 'You do not have permission to email invoices.',
                  anchorOrigin: { vertical: 'top', horizontal: 'right' }
                });
                return;
              }
              setSelectedInvoice(invoice);
              setOpenSendInvoicePdfDialog(true);
              handleCloseActionMenu();
            }}
          >
            <ListItemIcon>
              <AttachEmailIcon fontSize="small" />
            </ListItemIcon>
            Email Invoice
          </MenuItem>
        )}

        {invoice.status === INVOICE_STATUS.AWAITING_PAYMENT.id && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              if (!permissionCheck.canChangeInvoiceStatus()) {
                enqueueSnackbar({
                  variant: 'error',
                  message: 'You do not have permission to mark invoices as paid.',
                  anchorOrigin: { vertical: 'top', horizontal: 'right' }
                });
                return;
              }
              setSelectedInvoice(invoice);
              setOpenMarkAsPaidDialog?.(true);
              handleCloseActionMenu();
            }}
          >
            <ListItemIcon>
              <CheckCircle fontSize="small" />
            </ListItemIcon>
            Mark as Paid
          </MenuItem>
        )}

        {invoice.status === INVOICE_STATUS.AWAITING_PAYMENT.id && setOpenMarkAsDraftDialog && (
          // The row itself must be the menu's direct child, or arrow-key navigation skips it. A disabled row
          // ignores pointer events, so they are re-enabled for the tooltip and the click is guarded instead.
          <MenuItem
            disabled={Boolean(invoice.lockedAt)}
            sx={{ '&.Mui-disabled': { pointerEvents: 'auto' } }}
            onClick={(event) => {
              event.stopPropagation();
              if (invoice.lockedAt) return;
              if (!permissionCheck.canChangeInvoiceStatus()) {
                enqueueSnackbar({
                  variant: 'error',
                  message: 'You do not have permission to mark invoices as draft.',
                  anchorOrigin: { vertical: 'top', horizontal: 'right' }
                });
                return;
              }
              setSelectedInvoice(invoice);
              setOpenMarkAsDraftDialog(true);
              handleCloseActionMenu();
            }}
          >
            <Tooltip
              title={
                invoice.lockedAt
                  ? 'Invoice is locked. If corrections are required, you may create a credit note against this invoice and/or create a new invoice'
                  : ''
              }
            >
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon>
                  <EditNoteIcon fontSize="small" />
                </ListItemIcon>
                Mark as Draft
              </span>
            </Tooltip>
          </MenuItem>
        )}

        {invoice.status === INVOICE_STATUS.PAID.id && setOpenInvoicePaymentsDialog && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              if (!permissionCheck.canChangeInvoiceStatus() || !permissionCheck.canRefundPayment()) {
                enqueueSnackbar({
                  variant: 'error',
                  message: 'You do not have permission to refund invoices.',
                  anchorOrigin: { vertical: 'top', horizontal: 'right' }
                });
                return;
              }
              setSelectedInvoice(invoice);
              setOpenInvoicePaymentsDialog(true);
              handleCloseActionMenu();
            }}
          >
            <ListItemIcon>
              <Undo fontSize="small" />
            </ListItemIcon>
            Refund
          </MenuItem>
        )}

        {(invoice.status === INVOICE_STATUS.AWAITING_PAYMENT.id ||
          (invoice.status === INVOICE_STATUS.PAID.id && invoice.paymentOption !== INVOICE_PAYMENT.ONLINE)) && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              if (!permissionCheck.canChangeInvoiceStatus()) {
                enqueueSnackbar({
                  variant: 'error',
                  message: 'You do not have permission to void invoices.',
                  anchorOrigin: { vertical: 'top', horizontal: 'right' }
                });
                return;
              }
              setSelectedInvoice(invoice);
              setOpenVoidDialog?.(true);
              handleCloseActionMenu();
            }}
          >
            <ListItemIcon>
              <Block fontSize="small" />
            </ListItemIcon>
            Void
          </MenuItem>
        )}

        {invoice.status === INVOICE_STATUS.DRAFT.id && setOpenDeleteInvoiceDialog && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              if (!permissionCheck.check(CORE_PERMISSIONS.EDIT_INVOICE)) {
                enqueueSnackbar({
                  variant: 'error',
                  message: 'You do not have permission to delete invoices.',
                  anchorOrigin: { vertical: 'top', horizontal: 'right' }
                });
                return;
              }
              setSelectedInvoice(invoice);
              setOpenDeleteInvoiceDialog(true);
              handleCloseActionMenu();
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            Delete
          </MenuItem>
        )}
      </Menu>
    </div>
  );
};

export default ActionsColumn;
